import { pruneEditorStorage } from "../../../utils/editorStorage";

const STORAGE_KEY = "lawsigna:agreement-draft:v1";

/**
 * Separate slot per form. The legacy form is the same component in a different
 * mode, so without this a half-filled legacy contract and a half-filled normal
 * one would overwrite each other — and restoring one into the other would put
 * values in fields that mean something different.
 */
function keyFor(namespace?: string) {
  return namespace ? `${STORAGE_KEY}:${namespace}` : STORAGE_KEY;
}

export interface AgreementDraft {
  formData: any;
  activeStep: number;
  touched: Record<string, boolean>;
  savedAt: string;
  /** Set when the document body was left out of the draft to fit storage. */
  documentOmitted?: boolean;
}

/**
 * Fields that can't survive a page reload — File/Blob objects aren't
 * JSON-serializable, and the blob: URLs in `attachments` become invalid
 * once the tab that created them is gone. Everything else (including
 * documentSfdt, the editor's own JSON format) round-trips fine.
 */
function stripNonSerializable(formData: any) {
  if (!formData || typeof formData !== "object") return formData;
  const { uploadedFile, attachments, ...rest } = formData;
  return rest;
}

/**
 * Ceiling for a draft that still carries the document body. localStorage is
 * about 5 MB per origin and shared with everything else the app remembers
 * (filters, the editor's spell-check caches); an image-heavy document's
 * SFDT alone ran past that, and the next unrelated write — the editor's own
 * cache — failed with QuotaExceededError.
 */
const MAX_DRAFT_WITH_DOCUMENT_CHARS = 2 * 1024 * 1024;

function isQuotaError(e: unknown): boolean {
  return (
    e instanceof DOMException &&
    (e.name === "QuotaExceededError" ||
      e.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
      e.code === 22 ||
      e.code === 1014)
  );
}

export function saveAgreementDraft(
  draft: {
    formData: any;
    activeStep: number;
    touched: Record<string, boolean>;
  },
  namespace?: string,
) {
  try {
    const payload: AgreementDraft = {
      formData: stripNonSerializable(draft.formData),
      activeStep: draft.activeStep,
      touched: draft.touched,
      savedAt: new Date().toISOString(),
    };
    let serialized = JSON.stringify(payload);

    // Too big to keep the document: remember every other field and let
    // the document be re-picked, rather than fill the quota or save nothing.
    const withoutDocument = () => {
      const { documentSfdt, ...rest } = payload.formData ?? {};
      void documentSfdt;
      return JSON.stringify({
        ...payload,
        formData: rest,
        documentOmitted: true,
      });
    };
    if (serialized.length > MAX_DRAFT_WITH_DOCUMENT_CHARS) {
      serialized = withoutDocument();
    }

    try {
      localStorage.setItem(keyFor(namespace), serialized);
    } catch (e) {
      if (!isQuotaError(e)) throw e;
      localStorage.setItem(keyFor(namespace), withoutDocument());
    }
  } catch (e) {
    console.warn("Failed to save agreement draft to local storage:", e);
  }
}

export function loadAgreementDraft(namespace?: string): AgreementDraft | null {
  try {
    const raw = localStorage.getItem(keyFor(namespace));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.formData) return null;
    return parsed as AgreementDraft;
  } catch (e) {
    console.warn("Failed to load agreement draft from local storage:", e);
    return null;
  }
}

/**
 * Called once a contract is saved (and on "Discard & Start Fresh"): the
 * draft has served its purpose, and so has whatever the document editor
 * cached for that document, so both are dropped together.
 */
export function clearAgreementDraft(namespace?: string) {
  try {
    localStorage.removeItem(keyFor(namespace));
  } catch (e) {
    console.warn("Failed to clear agreement draft from local storage:", e);
  }
  pruneEditorStorage();
}

/**
 * Every remembered draft, of every form. Called when a user signs in or out:
 * drafts are keyed per form, not per person, so without this the next member
 * to sign in on the same browser resumed the previous member's half-filled
 * contract — including that member as the requester.
 */
export function clearAllLocalDrafts() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("lawsigna:") && k.includes("-draft"))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    console.warn("Failed to clear local drafts:", e);
  }
  pruneEditorStorage();
}
